import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { DictType } from "@pma/types";
import {
  SERVER_URL,
  STATUS_DICTIONARY_ENDPOINT,
  USER_STATUS_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { PlusCircleIcon } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { useRef, useState } from "react";
import { Input } from "@components/common/input";
import { Label } from "@components/common/label";
import { Switch } from "@components/common/switch";
import { Separator } from "@components/common/separator";
import { toast } from "@hooks/use-toast";
import { HexColorPicker } from "react-colorful";
import { useAuth } from "@stores/auth-store";
import useTranslator from "@hooks/use-translator";

interface Props {
  option: "task" | "project" | "user";
  revalidate?: CallableFunction;
}
const AddStatus = ({ option, revalidate }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<DictType>({
    status_name: "",
    color: "#6465ca",
    finished: false,
    canceled: false,
  });
  
  const colorPickerRef = useRef<HTMLDivElement | null>(null);
  
  const { translate } = useTranslator();
  const { authConfig } = useAuth();
  
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const userId = authConfig?.id;

  const forProject = option === "project";
  const forUser = option === "user";
  const forTask = option === "task";

  const addStatus = async (payload: DictType) => {
    const ENDPOINT =
      option === "user"
        ? USER_STATUS_DICTIONARY_ENDPOINT + "/add"
        : STATUS_DICTIONARY_ENDPOINT + "/add";
    const response = await axios.post(SERVER_URL + ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response?.data;
  };

  const addStatusMutation = useMutation({
    mutationKey: ["add-status", option],
    mutationFn: (payload: DictType) => addStatus(payload),
    onSuccess: (data) => {
      revalidate && revalidate();
      toast({
        title: translate("Success"),
        description: translate("Status added successfully"),
        variant: "success",
      });

      setStatus({ status_name: "", color: "#6465ca", finished: false, canceled: false });
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Error adding status"),
        variant: "error",
      });
    },
  });

  const onSubmit = () => {
    if (!status?.status_name) {
      return toast({
        title: translate("Info"),
        description: `${translate("Enter task title")} ${status?.status_name}`,
        variant: "info",
      });
    }

    const base = {
      ...status,
      organization_id: orgId,
      user_id: userId,
      is_active: true,
    };

    const payload =
      forTask || forProject
        ? {
            name: status?.status_name,
            canceled: status?.canceled ?? false,
            finished: status?.finished ?? false,
            for_task: forTask,
            for_project: forProject,
          }
        : {};

    return addStatusMutation.mutate({
      ...base,
      ...payload,
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"ghost"}
          size={"icon"}
          className="h-6 w-6 rounded-full"
        >
          <PlusCircleIcon className="text-primary" size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="">
        <div className="space-y-2">
          <h4 className="font-semibold">
            {translate("Add new")} {translate(option)} {translate("status")}
          </h4>
          <Separator />
          <div
            className="flex flex-col gap-2 w-full"
            onMouseEnter={() => setIsOpen(false)}
          >
            <h3 className="font-semibold text-xs">{translate("Name")}</h3>

            <Input
              placeholder={translate("Status")}
              className="w-full"
              required
              onChange={(e: any) =>
                setStatus((prev) => ({ ...prev, status_name: e.target.value }))
              }
            />
            {option !== "user" && (
              <>
                <h3 className="font-semibold text-xs">
                  {translate("Background Color")}
                </h3>
                <div className="relative">
                  <Button
                    className="w-full"
                    style={{
                      background: status?.color || "blue",
                    }}
                    onClick={() => setIsOpen(true)}
                  />
                  {isOpen && (
                    <div className="absolute" ref={colorPickerRef}>
                      <HexColorPicker
                        className=""
                        defaultValue={status?.color}
                        onChange={(e: any) =>
                          setStatus((prev) => ({ ...prev, color: e }))
                        }
                      />
                    </div>
                  )}
                </div>

                <Input
                  type="text"
                  pattern="^#+([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$"
                  placeholder="#0000ff"
                  value={status?.color}
                  onChange={(e: any) =>
                    setStatus((prev) => ({ ...prev, color: e.target.value }))
                  }
                />
              </>
            )}

            {option !== "user" && (
              <div className="flex flex-col gap-3 mt-2">
                <Separator />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col gap-0.5">
                    <Label className="text-xs font-semibold">{translate("Completion Status")}</Label>
                    <span className="text-[10px] text-muted-foreground">
                      {translate("Tasks with this status will be counted as completed")}
                    </span>
                  </div>
                  <Switch
                    checked={status?.finished ?? false}
                    onCheckedChange={(checked: boolean) =>
                      setStatus((prev) => ({
                        ...prev,
                        finished: checked,
                        canceled: checked ? false : prev.canceled,
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col gap-0.5">
                    <Label className="text-xs font-semibold">{translate("Cancellation Status")}</Label>
                    <span className="text-[10px] text-muted-foreground">
                      {translate("Tasks with this status will be counted as canceled")}
                    </span>
                  </div>
                  <Switch
                    checked={status?.canceled ?? false}
                    onCheckedChange={(checked: boolean) =>
                      setStatus((prev) => ({
                        ...prev,
                        canceled: checked,
                        finished: checked ? false : prev.finished,
                      }))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <PopoverClose className="w-full">
          <Button
            className="my-2 w-full"
            size={"sm"}
            onMouseEnter={() => setIsOpen(false)}
            onClick={onSubmit}
          >
            {translate("Add")}{" "}
          </Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};

export default AddStatus;
